import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Layout } from "@/components/Layout";
import { CarCard } from "@/components/CarCard";
import { SEO } from "@/components/SEO";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlidersHorizontal } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FleetPage() {
  const { t } = useLanguage();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [fuel, setFuel] = useState("all");
  const [transmission, setTransmission] = useState("all");
  const [listingType, setListingType] = useState("all");
  const [todayPrices, setTodayPrices] = useState({});

  useEffect(() => {
    axios.get(`${API}/pricing/today`).then((res) => setTodayPrices(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (fuel !== "all") params.set("fuel_type", fuel);
    if (transmission !== "all") params.set("transmission", transmission);
    if (listingType !== "all") params.set("listing_type", listingType);
    axios
      .get(`${API}/cars?${params.toString()}`)
      .then((res) => setCars(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, fuel, transmission, listingType]);

  const categories = ["all", "economy", "compact", "suv", "family", "premium", "luxury", "van", "minibus"];
  const fuels = ["all", "gasoline", "diesel", "hybrid", "lpg"];
  const transmissions = ["all", "manual", "automatic"];

  const categoryLabels = {
    all: "All",
    economy: t("fleet.economy") || "Economy",
    compact: t("fleet.compact") || "Compact",
    suv: t("fleet.suv") || "SUV",
    family: t("fleet.family") || "Family",
    premium: t("fleet.premium") || "Premium",
    luxury: t("fleet.luxury") || "Luxury",
    van: "Van",
    minibus: "Mini Bus",
  };

  return (
    <Layout>
      <SEO title="Our Fleet" description="Browse 30+ vehicles for rent in Thessaloniki. Economy, SUV, Premium & Luxury cars. Book online today." />
      <section className="py-12 md:py-20 bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h1 data-testid="fleet-title" className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight mb-3">
              {t("fleet.title")}
            </h1>
            <p className="font-dm-sans text-base md:text-lg text-slate-500">{t("fleet.subtitle")}</p>
          </div>

          {/* Filters */}
          <div data-testid="fleet-filters" className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-slate-100 mb-10">
            <div className="flex items-center gap-2 mb-4">
              <SlidersHorizontal className="w-5 h-5 text-slate-400" />
              <span className="font-outfit font-medium text-sm text-slate-700">{t("fleet.filters")}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="block font-outfit text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">{t("fleet.category")}</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="filter-category" className="h-11 bg-slate-50 border-slate-200 rounded-lg font-dm-sans text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>
                        {categoryLabels[c] || c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block font-outfit text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">{t("fleet.fuel")}</label>
                <Select value={fuel} onValueChange={setFuel}>
                  <SelectTrigger data-testid="filter-fuel" className="h-11 bg-slate-50 border-slate-200 rounded-lg font-dm-sans text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {fuels.map((f) => (
                      <SelectItem key={f} value={f}>{t(`fleet.${f}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block font-outfit text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">{t("fleet.transmission")}</label>
                <Select value={transmission} onValueChange={setTransmission}>
                  <SelectTrigger data-testid="filter-transmission" className="h-11 bg-slate-50 border-slate-200 rounded-lg font-dm-sans text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {transmissions.map((tr) => (
                      <SelectItem key={tr} value={tr}>{t(`fleet.${tr}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block font-outfit text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">Type</label>
                <Select value={listingType} onValueChange={setListingType}>
                  <SelectTrigger data-testid="filter-listing-type" className="h-11 bg-slate-50 border-slate-200 rounded-lg font-dm-sans text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="rent">For Rent</SelectItem>
                    <SelectItem value="sale">For Sale</SelectItem>
                    <SelectItem value="both">Rent & Sale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Car Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-2xl bg-white border border-slate-100 overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-slate-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 bg-slate-200 rounded w-3/4" />
                    <div className="h-4 bg-slate-100 rounded w-1/2" />
                    <div className="h-10 bg-slate-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : cars.length === 0 ? (
            <div data-testid="no-cars" className="text-center py-20">
              <p className="font-dm-sans text-lg text-slate-400">{t("fleet.noCars")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {cars.map((car) => (
                <CarCard key={car.id} car={car} effectivePrice={todayPrices[car.id]} />
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
